import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Building2,
  MapPin,
  Calendar,
  TrendingUp,
  Filter,
  Grid,
  List,
  Download
} from 'lucide-react';
import PromotionHeader from '../components/promotions/detail/PromotionHeader';
import PromotionGallery from '../components/promotions/detail/PromotionGallery';
import UnitsFilters from '../components/promotions/detail/UnitsFilters';
import UnitsGrid from '../components/promotions/detail/UnitsGrid';
import UnitDetailModal from '../components/promotions/detail/UnitDetailModal';

const PromotionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [promotion, setPromotion] = useState<any>(null);
  const [units, setUnits] = useState<any[]>([]);
  const [filteredUnits, setFilteredUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [quickStatusFilter, setQuickStatusFilter] = useState<string[]>([]);
  const [quickBlockFilter, setQuickBlockFilter] = useState<string>('');

  const [filters, setFilters] = useState({
    block_id: '',
    unit_type: '',
    status: '',
    min_area: '',
    max_area: '',
    min_floor: '',
    max_floor: '',
    orientation: '',
    view: '',
    has_balcony: false,
    has_parking: false,
    min_price: '',
    max_price: '',
  });

  useEffect(() => {
    fetchPromotion();
  }, [id]);

  useEffect(() => {
    if (promotion) {
      fetchUnits();
    }
  }, [promotion, filters]);

  useEffect(() => {
    let filtered = units;

    if (quickBlockFilter) {
      filtered = filtered.filter(u => u.block_id === parseInt(quickBlockFilter));
    }

    if (quickStatusFilter.length > 0) {
      filtered = filtered.filter(u => quickStatusFilter.includes(u.status));
    }

    setFilteredUnits(filtered);
  }, [quickStatusFilter, quickBlockFilter, units]);

  const fetchPromotion = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/promotions/${id}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPromotion(data);
    } catch (error) {
      console.error('Erreur chargement promotion:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });

      const response = await fetch(`${apiUrl}/promotions/${id}/units?${params}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setUnits(data);
      setFilteredUnits(data);
    } catch (error) {
      console.error('Erreur chargement lots:', error);
    }
  };

  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const toggleQuickStatus = (status: string) => {
    setQuickStatusFilter(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleUnitClick = (unit: any) => {
    setSelectedUnit(unit);
  };

  const handleUnitUpdate = async () => {
    await fetchUnits();
    await fetchPromotion();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!promotion) {
    return (
      <div className="p-6">
        <div className="text-center">
          <Building2 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Promotion non trouvée
          </h3>
          <Link
            to="/promotions"
            className="text-blue-600 hover:text-blue-700"
          >
            ← Retour aux promotions
          </Link>
        </div>
      </div>
    );
  }

  const realCounts = {
    available: units.filter(u => u.status === 'available').length,
    reserved: units.filter(u => u.status === 'reserved').length,
    sold: units.filter(u => u.status === 'sold').length,
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <PromotionHeader
        promotion={{...promotion, ...realCounts}}
        onUpdate={fetchPromotion}
      />

      {/* Galerie photos */}
      <PromotionGallery photos={promotion.photos || []} />

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filtres rapides par bloc */}
        {promotion.blocks && promotion.blocks.length > 1 && (
          <div className="mb-4 flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Bloc :</span>
            <button
              onClick={() => setQuickBlockFilter('')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                quickBlockFilter === ''
                  ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900 shadow-lg'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Tous
            </button>
            {promotion.blocks.map((block: any) => (
              <button
                key={block.id}
                onClick={() => setQuickBlockFilter(block.id.toString())}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  quickBlockFilter === block.id.toString()
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
                }`}
              >
                Bloc {block.block_name}
              </button>
            ))}
          </div>
        )}

        {/* Barre d'outils */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Tous les lots ({filteredUnits.length})
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              {(quickBlockFilter ? units.filter(u => u.block_id === parseInt(quickBlockFilter)) : units).filter(u => u.status === 'available').length} disponibles •{' '}
              {(quickBlockFilter ? units.filter(u => u.block_id === parseInt(quickBlockFilter)) : units).filter(u => u.status === 'reserved').length} réservés •{' '}
              {(quickBlockFilter ? units.filter(u => u.block_id === parseInt(quickBlockFilter)) : units).filter(u => u.status === 'sold').length} vendus
            </p>
          </div>

          <div className="flex items-center space-x-3">
            {/* Filtres rapides par statut */}
            <div className="flex items-center space-x-3">
              <label className="flex items-center space-x-2 cursor-pointer px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 transition-all hover:border-green-400 dark:hover:border-green-600">
                <input
                  type="checkbox"
                  checked={quickStatusFilter.includes('available')}
                  onChange={() => toggleQuickStatus('available')}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-green-700 dark:text-green-300">Disponible</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer px-3 py-2 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 transition-all hover:border-orange-400 dark:hover:border-orange-600">
                <input
                  type="checkbox"
                  checked={quickStatusFilter.includes('reserved')}
                  onChange={() => toggleQuickStatus('reserved')}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm font-medium text-orange-700 dark:text-orange-300">Réservé</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 transition-all hover:border-blue-400 dark:hover:border-blue-600">
                <input
                  type="checkbox"
                  checked={quickStatusFilter.includes('sold')}
                  onChange={() => toggleQuickStatus('sold')}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Vendu</span>
              </label>
            </div>

            {/* Séparateur */}
            <div className="h-8 w-px bg-gray-300 dark:bg-gray-600"></div>

            {/* Bouton filtres */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                showFilters
                  ? 'bg-green-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>Filtres</span>
            </button>

            {/* Vue grille/tableau */}
            <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 ${
                  viewMode === 'grid'
                    ? 'bg-green-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 ${
                  viewMode === 'table'
                    ? 'bg-green-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Filtres sidebar */}
          {showFilters && (
            <div className="w-80 flex-shrink-0">
              <UnitsFilters
                filters={filters}
                blocks={promotion.blocks || []}
                onChange={handleFilterChange}
              />
            </div>
          )}

          {/* Grille des lots */}
          <div className="flex-1">
            <UnitsGrid
              units={filteredUnits}
              viewMode={viewMode}
              onUnitClick={handleUnitClick}
            />
          </div>
        </div>
      </div>

      {/* Modal détail lot */}
      {selectedUnit && (
        <UnitDetailModal
          unit={selectedUnit}
          onClose={() => setSelectedUnit(null)}
          onUpdate={handleUnitUpdate}
        />
      )}
    </div>
  );
};

export default PromotionDetailPage;
